import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";

import "../../chart/registerChartJs";
import { getColors } from "../../helpers/commonHelper";
import { formatPortfolioUsdAmount } from "../../pages/Portfolio/chartDataUtils";

function formatTokenAmt(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x === 0) return "0";
  if (Math.abs(x) < 1e-6) return x.toExponential(2);
  return x.toLocaleString("en-US", { maximumFractionDigits: 8 });
}

function allocationTooltipExternal(context, entries, useUsdSlices) {
  const { chart, tooltip } = context;
  const parent = chart.canvas.parentNode;
  if (!parent) return;

  let el = parent.querySelector(".portfolio-allocation-tooltip");
  if (!el) {
    el = document.createElement("div");
    el.className = "portfolio-allocation-tooltip";
    el.setAttribute("role", "tooltip");
    parent.appendChild(el);
  }

  if (tooltip.opacity === 0) {
    el.style.opacity = "0";
    return;
  }

  const dp = tooltip.dataPoints?.[0];
  if (!dp) return;

  const idx = dp.dataIndex;
  const entry = entries[idx];
  const dataset = chart.data.datasets[0];
  const values = (dataset.data || []).map((x) => Number(x));
  const total = values.reduce((a, b) => a + b, 0);
  const v = values[idx];
  const pct = total ? ((v / total) * 100).toFixed(2) : "0";
  const bg = Array.isArray(dataset.backgroundColor)
    ? dataset.backgroundColor[idx]
    : dataset.backgroundColor;

  const title = (entry?.coinSymbol || entry?.asset_name || "").trim() || "—";
  const balanceText = useUsdSlices ? formatPortfolioUsdAmount(v) : formatTokenAmt(v);
  const pctOnly = `${pct}%`;

  el.replaceChildren();
  const card = document.createElement("div");
  card.className = "portfolio-allocation-tooltip__card";

  const titleRow = document.createElement("div");
  titleRow.className = "portfolio-allocation-tooltip__titleRow";

  const titleGroup = document.createElement("div");
  titleGroup.className = "portfolio-allocation-tooltip__titleGroup";

  if (entry?.imagePath) {
    const img = document.createElement("img");
    img.className = "portfolio-allocation-tooltip__coin";
    img.src = entry.imagePath;
    img.alt = "";
    img.loading = "lazy";
    img.referrerPolicy = "no-referrer";
    titleGroup.appendChild(img);
  }

  const titleEl = document.createElement("span");
  titleEl.className = "portfolio-allocation-tooltip__title";
  titleEl.textContent = title;

  const balanceEl = document.createElement("span");
  balanceEl.className = "portfolio-allocation-tooltip__balance";
  balanceEl.textContent = balanceText;

  titleGroup.appendChild(titleEl);
  titleRow.appendChild(titleGroup);
  titleRow.appendChild(balanceEl);

  const metricsRow = document.createElement("div");
  metricsRow.className = "portfolio-allocation-tooltip__metricsRow";

  const sliceSwatch = document.createElement("span");
  sliceSwatch.className = "portfolio-allocation-tooltip__sliceSwatch";
  sliceSwatch.style.background = typeof bg === "string" ? bg : "#888";
  metricsRow.appendChild(sliceSwatch);

  const metricsEl = document.createElement("span");
  metricsEl.className = "portfolio-allocation-tooltip__metrics";
  metricsEl.textContent = pctOnly;

  metricsRow.appendChild(metricsEl);
  card.appendChild(titleRow);
  card.appendChild(metricsRow);
  el.appendChild(card);

  el.style.opacity = "1";
  el.style.position = "absolute";
  el.style.left = `${tooltip.caretX + chart.canvas.offsetLeft}px`;
  el.style.top = `${tooltip.caretY + chart.canvas.offsetTop}px`;
  el.style.transform = "translate(-50%, calc(-100% - 10px))";
  el.style.pointerEvents = "none";
  el.style.zIndex = "25";
}

function PortfolioAssetAllocationChart(props) {
  const { assetWalletData, assetTotalUsdValue } = props;

  const palette = useMemo(() => getColors(), []);

  const useUsdSlices = useMemo(
    () => assetWalletData?.some((x) => Number(x.usd_value) > 0) ?? false,
    [assetWalletData],
  );

  const entries = useMemo(() => {
    if (!assetWalletData?.length) return [];
    if (useUsdSlices) {
      return assetWalletData.filter((ele) => Number(ele.usd_value) > 0);
    }
    return assetWalletData.filter((ele) => Number(ele.tokenBalance) > 0);
  }, [assetWalletData, useUsdSlices]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      resizeDelay: 120,
      cutout: "80%",
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: (ctx) => allocationTooltipExternal(ctx, entries, useUsdSlices),
        },
      },
    }),
    [entries, useUsdSlices],
  );

  const data = useMemo(
    () => ({
      labels: entries.map((e) => e.asset_name),
      datasets: [
        {
          data: entries.map((e) =>
            useUsdSlices ? Number(e.usd_value) : Number(e.tokenBalance),
          ),
          backgroundColor: entries.map((_, index) => palette[index % palette.length]),
          borderWidth: 0,
          hoverOffset: 0,
        },
      ],
    }),
    [entries, palette, useUsdSlices],
  );

  if (!entries.length) {
    const hasRows = assetWalletData?.length > 0;
    return (
      <div className="atsPiechart pieChartEmpty d-flex align-items-center justify-content-center text-center">
        {hasRows
          ? useUsdSlices
            ? "No assets with a positive balance to show in allocation."
            : "No token amounts available to show in allocation."
          : "No asset allocation to display."}
      </div>
    );
  }

  return (
    <div className="atsPiechart">
      <div className="pieDonutWrap position-relative">
        <Doughnut data={data} options={chartOptions} />
        <div className="pieCenterLabel text-center">
          <div className="pieCenterLabelTitle">Portfolio Balance</div>
          <div className="pieCenterLabelValue">
            <span className="pieCenterLabelAmount">
              {formatPortfolioUsdAmount(assetTotalUsdValue)}
            </span>
          </div>
        </div>
      </div>
      <ul
        className={`pieCustomLegend list-unstyled mb-0${
          entries.length > 5 ? " pieCustomLegend--scrollable" : ""
        }`}
      >
        {entries.map((ele, index) => {
          const displaySymbol = (ele.coinSymbol || ele.asset_name || "").trim() || "—";
          return (
            <li
              key={`${ele.asset_name}-${index}`}
              className="pieLegendRow d-flex align-items-center"
            >
              <span
                className="pieSwatch"
                style={{ backgroundColor: palette[index % palette.length] }}
              />
              <span className="pieLegendSymbol">{displaySymbol}</span>
              {useUsdSlices && Number(ele.usd_value) > 0 && (
                <span className="pieLegendQty">
                  {formatPortfolioUsdAmount(ele.usd_value)}
                </span>
              )}
              {!useUsdSlices && Number(ele.tokenBalance) > 0 && (
                <span className="pieLegendQty">
                  {formatTokenAmt(ele.tokenBalance)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default PortfolioAssetAllocationChart;
